import React, { useEffect, useRef, useState } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import { Bar } from "react-chartjs-2";
import { request } from "../../../../request";

// Register required components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const EarningsChart = ({ data, startDate, endDate, inputs, getFilteredHistory }) => {
    // Transform the data into labels (dates) and datasets (profits and losses)
    const labels = data.map((item) => item.date);
    const profits = data.map((item) => item.profit);
    const losses = data.map((item) => item.loss);
    const income = data.map((item) => item.income);
    const hrach = data.map((item) => item.hrach);
    const fahad = data.map((item) => item.fahad);
    const artyom = data.map((item) => item.artyom);
    const hrach_given = data.map((item) => -1 * item.hrach_given);
    const artyom_given = data.map((item) => -1 * item.artyom_given);
    const fahad_given = data.map((item) => -1 * item.fahad_given);
    const [disabled, setDisabled] = useState({});
    const prevValues = useRef("");

    useEffect(() => {
        const newValues = JSON.stringify([disabled, startDate, endDate, inputs]);
        if (newValues !== prevValues.current) {
            console.log(disabled, startDate, endDate);
            getFilteredHistory();
            prevValues.current = newValues;
        }
    }, [disabled, startDate, endDate, inputs]);

    const arr_hrach = [
        {
            label: "Hrach",
            data: hrach, // Y-axis values for personal spendings
            backgroundColor: "rgba(0, 0, 255, 0.5)", // Light yellow
            borderColor: "rgba(0, 0, 255, 1)", // Bright yellow
            borderWidth: 1,
            stack: "hrach",
            hidden: disabled["Hrach"] || false,
        },
        {
            label: "Hrach Given",
            data: hrach_given, // Y-axis values for personal spendings
            backgroundColor: "rgba(0, 0, 255, 1)", // Light yellow
            borderColor: "rgba(0, 0, 255, 1)", // Bright yellow
            borderWidth: 1,
            stack: "hrach",
            hidden: disabled["Hrach Given"] || false,
        },
        {
            label: "Artyom",
            data: artyom, // Y-axis values for personal spendings
            backgroundColor: "rgba(3, 252, 78, 0.5)", // Light yellow
            borderColor: "rgba(3, 252, 78, 1)", // Bright yellow
            borderWidth: 1,
            stack: "artyom",
            hidden: disabled["Artyom"] || false,
        },
        {
            label: "Artyom Given",
            data: artyom_given, // Y-axis values for personal spendings
            backgroundColor: "rgba(3, 252, 78, 1)", // Light yellow
            borderColor: "rgba(3, 252, 78, 1)", // Bright yellow
            borderWidth: 1,
            stack: "artyom",
            hidden: disabled["Artyom Given"] || false,
        },
        {
            label: "Fahad",
            data: fahad, // Y-axis values for personal spendings
            backgroundColor: "rgba(252, 94, 3, 0.5)", // Light yellow
            borderColor: "rgba(252, 94, 3, 1)", // Bright yellow
            borderWidth: 1,
            stack: "fahad",
            hidden: disabled["Fahad"] || false,
        },
        {
            label: "Fahad Given",
            data: fahad_given, // Y-axis values for personal spendings
            backgroundColor: "rgba(252, 94, 3, 1)", // Light yellow
            borderColor: "rgba(252, 94, 3, 1)", // Bright yellow
            borderWidth: 1,
            stack: "fahad",
            hidden: disabled["Fahad Given"] || false,
        },
    ];

    const arr_getcoin = [
        {
            label: "Getcoin",
            data: hrach, // Y-axis values for personal spendings
            backgroundColor: "rgba(0, 0, 255, 0.5)", // Light yellow
            borderColor: "rgba(0, 0, 255, 1)", // Bright yellow
            borderWidth: 1,
            stack: "hrach",
            hidden: disabled["Hrach"] || false,
        },
        {
            label: "Getcoin Given",
            data: hrach_given, // Y-axis values for personal spendings
            backgroundColor: "rgba(0, 0, 255, 1)", // Light yellow
            borderColor: "rgba(0, 0, 255, 1)", // Bright yellow
            borderWidth: 1,
            stack: "hrach",
            hidden: disabled["Hrach Given"] || false,
        },
    ];

    const arr = process.env.REACT_APP_GETCOIN ? arr_getcoin : arr_hrach;
    // Chart.js configuration
    const chartData = {
        labels, // X-axis labels (dates)
        datasets: [
            {
                label: "Profits",
                data: profits, // Y-axis values for profits
                backgroundColor: "rgba(75, 192, 192, 1)", // Light teal
                borderColor: "rgba(75, 192, 192, 1)", // Dark teal
                borderWidth: 1,
                stack: "Stack 0",
                hidden: disabled["Profits"] || false,
            },
            {
                label: "Losses",
                data: losses, // Y-axis values for losses
                backgroundColor: "rgba(255, 99, 132, 1)", // Light red
                borderColor: "rgba(255, 99, 132, 1)", // Dark red
                borderWidth: 1,
                stack: "Stack 0",
                hidden: disabled["Losses"] || false,
            },
            {
                label: "Income",
                data: income, // Y-axis values for personal spendings
                backgroundColor: "rgba(255, 206, 86, 1)", // Light yellow
                borderColor: "rgba(255, 206, 86, 1)", // Bright yellow
                borderWidth: 1,
                stack: "Stack 0",
                hidden: disabled["Income"] || false,
            },
            ...arr,
        ],
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                onClick: (chart, legendItem) => {
                    const label = legendItem.text;

                    setDisabled((prev) => ({
                        ...prev,
                        [label]: !prev[label],
                    }));
                },
                display: window.innerWidth > 768,
                position: "top",
            },
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: "Date",
                },
                stacked: true,
            },
            y: {
                grid: {
                    color: (context) => (context.tick.value === 0 ? "white" : "rgba(0, 0, 0, 0.1)"),
                    lineWidth: (context) => (context.tick?.value === 0 ? 3 : 1),
                },
                ticks: {
                    color: "white",
                },
                title: {
                    display: true,
                    text: "Amount (AMD)",
                },
                stacked: (context) => {
                    return false;
                },
            },
        },
    };

    return (
        <div
            style={{
                width: "100%",
                maxWidth: "1000px",
                // minWidth: "1000px",
                height: "400px",
                overflowX: "auto", // Enable horizontal scrolling if the content overflows // For smooth scrolling on iOS
            }}
        >
            <Bar data={chartData} options={options} />
        </div>
    );
};

export default EarningsChart;
